import React, { useState, useEffect } from "react";

import Header from "./Header.jsx";
import NavigationButtons from "./NavigationButtons.jsx";
import Panel from "../../shared/bootstrap/Panel.jsx";
import PanelHeader from "../../shared/bootstrap/PanelHeader.jsx";
import GeneralInfo from "./GeneralInfo.jsx";
import Contacts from "./Contacts.jsx";
import Addresses from "./Addresses.jsx";

import { getContractHolderDetails } from "../../../api/contractHolders.js";
import { getDisplayType } from "../../../util/contractHoldersList.js";

/**
 * ContractHoldersDetails Component
 * @param {Object} props
 * @param {string} props.path
 * @param {Function} props.setFetchError
 * @returns {React.JSX.Element}
 */
export default function ContractHoldersDetails({ path, setFetchError }) {
  /** @type {[ContractHolderDetails | {}, React.Dispatch<React.SetActionState<ContractHolderDetails>>]} */
  const [contractHolderDetailsData, setContractHoldersDetailsData] = useState({});

  //get contract holder details data if needed
  useEffect(() => {
    const display = getDisplayType(path);
    // only get contract holder detail data if on a detail page AND we don't already have it
    if (contractHolderDetailsData.chInfoSeq !== display.id) {
      getContractHolderDetails(display.id)
        .then(
          /** @param {ContractHolderDetails} contractHolderDetailsResponse */
          (contractHolderDetailsResponse) => {
            setContractHoldersDetailsData(contractHolderDetailsResponse);
          }
        )
        .catch((error) => {
          setFetchError(true);
        });
    }
  }, [path]);

  useEffect(() => {
    const { id } = getDisplayType(path);
    if (id && contractHolderDetailsData && contractHolderDetailsData.companyName) {
      document.title = `NASA SEWP Contract Holders - ${contractHolderDetailsData.companyName}`;
    }
  }, [contractHolderDetailsData]);

  if (!contractHolderDetailsData || !contractHolderDetailsData.chInfoSeq)
    return <div>Loading Contract Holder Data...</div>;

  // using an onClick instead of href b/c url already contains a '#'
  const scrollToTop = () => document.getElementById("TOP").scrollIntoView();
  const scrollToTopElement = (
    <div className="text-center link clickable" role="link">
      <a onClick={scrollToTop}>Back To Top</a>
    </div>
  );

  return (
    <div className="container-fluid" id="inner">
      <div id="TOP"></div>
      <Header name={contractHolderDetailsData.companyName} image={contractHolderDetailsData.logoFile} />
      <NavigationButtons />

      <Panel id="CH_geninfo" primary={true}>
        <PanelHeader title="General Info" />
        <GeneralInfo
          displayName={contractHolderDetailsData.displayName}
          companyName={contractHolderDetailsData.companyName}
          corpUrl={contractHolderDetailsData.corpUrl}
          duns={contractHolderDetailsData.duns}
          uei={contractHolderDetailsData.uei}
          tin={contractHolderDetailsData.tin}
          cage={contractHolderDetailsData.cage}
          facets={contractHolderDetailsData.facets}
          ottpFacet={contractHolderDetailsData.ottpFacet}
          certifiedUrl={contractHolderDetailsData.certifiedUrl}
          certifiedLogoUrl={contractHolderDetailsData.certifiedLogoUrl}
          contracts={contractHolderDetailsData.contracts}
        />
      </Panel>

      {scrollToTopElement}

      <div id="CH_contacts" className="panel panel-primary table-responsive">
        <PanelHeader title="Contacts" />
        <Contacts companyName={contractHolderDetailsData.companyName} contacts={contractHolderDetailsData.contacts} />
      </div>

      {scrollToTopElement}

      <Panel id="CH_address" primary={true}>
        <PanelHeader title="Addresses" />
        <Addresses addresses={contractHolderDetailsData.addresses} />
      </Panel>

      {scrollToTopElement}
    </div>
  );
}
